import { POST_GRADUATION_OPTION_LABELS, POST_GRADUATION_OPTION_VALUES } from './general';

export const AUDIENCE = {
  HOUSING: 'housing',
  FHP: 'fhp',
  FOUR_YEAR_COLLEGE: POST_GRADUATION_OPTION_VALUES.FOUR_YEAR_COLLEGE,
  TWO_YEAR_COLLEGE: POST_GRADUATION_OPTION_VALUES.TWO_YEAR_COLLEGE,
  MILITARY: POST_GRADUATION_OPTION_VALUES.MILITARY,
  WORKFORCE: POST_GRADUATION_OPTION_VALUES.WORKFORCE,
};

export const CATEGORY = {
  ACADEMIC_CAREER: 'Academic & Career Readiness',
  INTERPERSONAL: 'Interpersonal & Communication Skills',
  CHARACTER_LEADERSHIP: 'Character & Leadership',
  ADAPTABILITY: 'Adaptability & Resilience',
  FOUR_YEAR: POST_GRADUATION_OPTION_LABELS[POST_GRADUATION_OPTION_VALUES.FOUR_YEAR_COLLEGE],
  TWO_YEAR: POST_GRADUATION_OPTION_LABELS[POST_GRADUATION_OPTION_VALUES.TWO_YEAR_COLLEGE],
  MILITARY: POST_GRADUATION_OPTION_LABELS[POST_GRADUATION_OPTION_VALUES.MILITARY],
  WORKFORCE: POST_GRADUATION_OPTION_LABELS[POST_GRADUATION_OPTION_VALUES.WORKFORCE],
};

export const recommendationQuestionnaires = [
  {
    audience: AUDIENCE.HOUSING,
    categories: [
      {
        name: CATEGORY.ACADEMIC_CAREER,
        questions: [
          { id: 'academic.workEthic', question: 'The applicant demonstrates a strong work ethic' },
          {
            id: 'academic.meetDemands',
            question: 'The applicant has the skills to meet the demands of college, trade training, the military, or the workforce',
          },
          { id: 'academic.initiativeProblemSolving', question: 'The applicant shows initiative and problem-solving skills' },
          { id: 'academic.timeManagement', question: 'The applicant demonstrates strong time management skills' },
          { id: 'academic.persistenceChallenges', question: 'The applicant shows persistence when faced with challenges' },
          { id: 'academic.reliabilityTasks', question: 'The applicant has shown reliability in completing tasks or responsibilities' },
          { id: 'academic.techComfort', question: 'The applicant is comfortable using technology to support learning or work' },
        ],
      },
      {
        name: CATEGORY.INTERPERSONAL,
        questions: [
          { id: 'interpersonal.communication', question: 'The applicant communicates effectively with peers and adults' },
          { id: 'interpersonal.teamwork', question: 'The applicant works well in team settings and collaborates effectively' },
        ],
      },
      {
        name: CATEGORY.CHARACTER_LEADERSHIP,
        questions: [
          { id: 'character.goalSetting', question: 'The applicant sets clear goals and takes steps to achieve them' },
          { id: 'character.openToFeedback', question: 'The applicant is open to feedback and uses it to improve' },
          { id: 'character.integrityHonesty', question: 'The applicant demonstrates integrity and honesty in their actions' },
          { id: 'character.positiveLeadership', question: 'The applicant shows positive leadership qualities in academic or community settings' },
          { id: 'character.communityContribution', question: 'The applicant demonstrates a commitment to contributing to their community' },
          {
            id: 'character.resistPeerPressureSubstance',
            question: 'The applicant demonstrates the ability to resist peer pressure in situations involving substance use.',
          },
          {
            id: 'character.commitmentSobriety',
            question: 'The applicant shows a strong commitment to maintaining sobriety as part of their personal and academic goals.',
          },
        ],
      },
      {
        name: CATEGORY.ADAPTABILITY,
        questions: [
          { id: 'adaptability.calmUnderPressure', question: 'The applicant remains calm under pressure' },
          { id: 'adaptability.adjustsToNewEnvironments', question: 'The applicant adjusts well to new environments or responsibilities' },
          { id: 'adaptability.adaptsToChallenges', question: 'The applicant adapts well to new challenges' },
        ],
      },
    ],
  },
  {
    audience: AUDIENCE.FHP,
    categories: [
      {
        name: CATEGORY.ADAPTABILITY,
        questions: [
          { id: 'fhp.calmUnderPressure', question: 'The applicant remains calm under pressure' },
          { id: 'fhp.adjustsToNewEnvironments', question: 'The applicant adjusts well to new environments or responsibilities' },
          { id: 'fhp.adaptsToChallenges', question: 'The applicant adapts well to new challenges' },
          {
            id: 'fhp.consistentDailyRoutine',
            question: 'The applicant independently maintains a consistent daily routine (e.g., sleep schedule, meals, responsibilities).',
          },
          { id: 'fhp.personalHygiene', question: 'The applicant demonstrates personal hygiene habits appropriate for independent living.' },
          {
            id: 'fhp.managesLivingSpace',
            question: 'The applicant manages their own personal living space by maintaining their room, bathroom, laundry, etc., without reminders',
          },
          { id: 'fhp.basicMealsNutrition', question: 'The applicant is capable of preparing basic meals and applying basic nutrition principles' },
          { id: 'fhp.medicalAppointments', question: 'The applicant knows how to schedule and attend medical appointments independently' },
          {
            id: 'fhp.prescriptionRefills',
            question: 'The applicant has practiced how to request prescription refills and asks for help to follow up on medical needs',
          },
          {
            id: 'fhp.transportationProblemSolving',
            question: 'The applicant independently problem-solves their own transportation or makes arrangements reliably',
          },
          {
            id: 'fhp.confidenceForHousingProgram',
            question:
              'I am confident that this applicant has the independence to benefit from and contribute to the Successful Futures Housing program',
          },
        ],
      },
    ],
  },
  {
    audience: AUDIENCE.FOUR_YEAR_COLLEGE,
    categories: [
      {
        name: CATEGORY.FOUR_YEAR,
        questions: [
          { id: 'fourYear.academicRigorPrepared', question: 'The applicant is well-prepared to handle the academic rigor of a university program' },
          { id: 'fourYear.studyHabits', question: 'The applicant demonstrates effective study habits' },
          {
            id: 'fourYear.socialCriticalHelp',
            question:
              'The applicant consistently demonstrates higher-level social skills, the ability to think critically, and to ask for help when needed',
          },
          { id: 'fourYear.potentialForSuccess', question: 'The applicant shows strong potential for success in a four-year college environment' },
        ],
      },
    ],
  },
  {
    audience: AUDIENCE.TWO_YEAR_COLLEGE,
    categories: [
      {
        name: CATEGORY.TWO_YEAR,
        questions: [
          {
            id: 'twoYear.preparedReadingMathTech',
            question: 'The applicant is prepared in reading, math, and technology for the program they are considering.',
          },
          { id: 'twoYear.studyHabits', question: 'The applicant demonstrates effective study habits' },
          {
            id: 'twoYear.socialProblemSolveNavigate',
            question:
              'The applicant consistently demonstrates good social skills, can problem solve, and have the ability to navigate a 2-year college challenges',
          },
          { id: 'twoYear.careerOrientedCoursework', question: 'The applicant is ready for focused, career-oriented coursework' },
        ],
      },
    ],
  },
  {
    audience: AUDIENCE.MILITARY,
    categories: [
      {
        name: CATEGORY.MILITARY,
        questions: [
          { id: 'military.disciplineRespect', question: 'The applicant demonstrates discipline and respect for authority and rules' },
          { id: 'military.resilienceUnderPressure', question: 'The applicant has shown resilience under pressure' },
          {
            id: 'military.readinessChallenges',
            question: 'The applicant demonstrates physical and emotional readiness for the challenges of military service',
          },
          { id: 'military.preparedStructuredDemanding', question: 'The applicant is prepared for structured and demanding environments' },
        ],
      },
    ],
  },
  {
    audience: AUDIENCE.WORKFORCE,
    categories: [
      {
        name: CATEGORY.WORKFORCE,
        questions: [
          { id: 'workforce.bestOption', question: 'Entering the workforce is the best option for this applicant after graduation' },
          {
            id: 'workforce.selfMotivationReliability',
            question: 'The applicant exhibits self-motivation, reliability and the willingness to put forth efforts to succeed in a work setting',
          },
          {
            id: 'workforce.motivatedIncomeEffort',
            question:
              'The applicant is motivated by earning an income and it is willing the applicant displays consistent effort in a positive attitude toward completing assigned tasks.',
          },
          {
            id: 'workforce.independentResponsibility',
            question: 'The applicant can work independently and take responsibility for their performance',
          },
        ],
      },
    ],
  },
];

export const ratingScale = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree', 'NA'];

export const ratingPoints = {
  'Strongly Agree': 5,
  'Agree': 4,
  'Neutral': 3,
  'Disagree': 2,
  'Strongly Disagree': 1,
  NA: 0,
};
